import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { REVIEW_QUEUE } from '../lib/mock-data';
import { CheckSquare, AlertTriangle, Check, X, Eye, Filter, Clock, FileText, User, ChevronRight, AlertCircle, FileSearch, Tag, Shield, PenTool, GitMerge } from 'lucide-react';
export function ReviewQueue() {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<string | null>(REVIEW_QUEUE[0]?.id || null);
  const selectedReview = REVIEW_QUEUE.find(r => r.id === selectedItem);
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Chronology Conflict':
        return <GitMerge className="w-4 h-4" data-id="element-1989" />;
      case 'Classification':
        return <Tag className="w-4 h-4" data-id="element-1990" />;
      case 'Draft Review':
        return <PenTool className="w-4 h-4" data-id="element-1991" />;
      case 'Privilege Flag':
        return <Shield className="w-4 h-4" data-id="element-1992" />;
      default:
        return <FileText className="w-4 h-4" data-id="element-1993" />;
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Chronology Conflict':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Classification':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Draft Review':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Privilege Flag':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'bg-red-500';
      case 'Medium':
        return 'bg-amber-500';
      case 'Low':
        return 'bg-emerald-500';
      default:
        return 'bg-muted';
    }
  };
  const filteredQueue = selectedFilter === 'all' ? REVIEW_QUEUE : REVIEW_QUEUE.filter(r => r.type === selectedFilter);
  const typeCounts = REVIEW_QUEUE.reduce((acc, item) => {
    acc[item.type] = (acc[item.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  return <div className="space-y-6 h-[calc(100vh-8rem)] flex flex-col" data-id="element-1994">
      {/* Header */}
      <div className="shrink-0" data-id="element-1995">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1996">
          <div data-id="element-1997">
            <h1 className="text-2xl font-bold tracking-tight flex items-center" data-id="element-1998">
              <CheckSquare className="w-6 h-6 mr-3 text-amber-600" data-id="element-1999" />
              Review Queue
            </h1>
            <p className="text-muted-foreground mt-1" data-id="element-2000">
              Human-in-the-loop approval checkpoints for AI-generated outputs.
              All items require attorney review.
            </p>
          </div>
          <div className="flex items-center space-x-3" data-id="element-2001">
            <Badge variant="outline" className="px-3 py-1.5 text-sm bg-amber-50 text-amber-800 border-amber-200" data-id="element-2002">

              <AlertCircle className="w-4 h-4 mr-1.5" data-id="element-2003" />
              {REVIEW_QUEUE.length} Pending
            </Badge>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="shrink-0 flex flex-wrap gap-2 border-b pb-4" role="tablist" aria-label="Review queue filters" data-id="element-2004">
        <button role="tab" aria-selected={selectedFilter === 'all'} onClick={() => setSelectedFilter('all')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${selectedFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`} data-id="element-2005">

          All ({REVIEW_QUEUE.length})
        </button>
        {Object.entries(typeCounts).map(([type, count]) => <button key={type} role="tab" aria-selected={selectedFilter === type} onClick={() => setSelectedFilter(type)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center space-x-1.5 ${selectedFilter === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`} data-id="element-2006">

            {getTypeIcon(type)}
            <span data-id="element-2007">{type}</span>
            <span className="ml-1" data-id="element-2008">({count})</span>
          </button>)}
      </div>

      {/* Two-Panel Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0 overflow-hidden" data-id="element-2009">
        {/* Left Panel: Queue List */}
        <div className="lg:col-span-5 flex flex-col overflow-hidden" data-id="element-2010">
          <Card className="flex-1 flex flex-col overflow-hidden shadow-sm" data-id="element-2011">
            <CardHeader className="py-3 border-b shrink-0 bg-muted/30" data-id="element-2012">
              <div className="flex items-center justify-between" data-id="element-2013">
                <CardTitle className="text-sm font-medium" data-id="element-2014">
                  Pending Items
                </CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs" data-id="element-2015">
                  <Filter className="w-3 h-3 mr-1" data-id="element-2016" /> Sort
                </Button>
              </div>
            </CardHeader>
            <div className="flex-1 overflow-y-auto" data-id="element-2017">
              <div className="divide-y" data-id="element-2018">
                {filteredQueue.map(item => {
                const isSelected = item.id === selectedItem;
                return <div key={item.id} onClick={() => setSelectedItem(item.id)} className={`p-4 cursor-pointer transition-colors border-l-4 ${isSelected ? 'bg-blue-50 border-l-blue-500' : 'hover:bg-muted/30 border-l-transparent'}`} data-id="element-2019">

                      <div className="flex items-start justify-between mb-2" data-id="element-2020">
                        <div className="flex items-center space-x-2" data-id="element-2021">
                          <div className={`w-2 h-2 rounded-full ${getPriorityColor(item.priority)}`} title={`${item.priority} Priority`} data-id="element-2022" />

                          <Badge variant="outline" className={`text-[10px] ${getTypeColor(item.type)}`} data-id="element-2023">

                            {item.type}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground" data-id="element-2024">
                          {item.date}
                        </span>
                      </div>
                      <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-1" data-id="element-2025">
                        {item.task}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate mb-2" data-id="element-2026">
                        {item.matter}
                      </p>
                      <div className="flex items-center justify-between" data-id="element-2027">
                        <div className="flex items-center space-x-1 text-xs text-muted-foreground" data-id="element-2028">
                          <User className="w-3 h-3" data-id="element-2029" />
                          <span data-id="element-2030">{item.assignedTo}</span>
                        </div>
                        <Badge variant={item.aiConfidence > 85 ? 'success' : item.aiConfidence > 70 ? 'warning' : 'destructive'} className="text-[10px]" data-id="element-2031">

                          {item.aiConfidence}% Conf.
                        </Badge>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </Card>
        </div>

        {/* Right Panel: Detail View */}
        <div className="lg:col-span-7 flex flex-col overflow-hidden" data-id="element-2032">
          {selectedReview ? <Card className="flex-1 flex flex-col overflow-hidden shadow-md border-l-4 border-l-amber-500" data-id="element-2033">
              <CardHeader className="py-4 border-b shrink-0 bg-amber-50/30" data-id="element-2034">
                <div className="flex items-start justify-between" data-id="element-2035">
                  <div data-id="element-2036">
                    <div className="flex items-center space-x-2 mb-2" data-id="element-2037">
                      <Badge variant="outline" className={`${getTypeColor(selectedReview.type)}`} data-id="element-2038">

                        {getTypeIcon(selectedReview.type)}
                        <span className="ml-1" data-id="element-2039">{selectedReview.type}</span>
                      </Badge>
                      <Badge variant={selectedReview.priority === 'High' ? 'destructive' : selectedReview.priority === 'Medium' ? 'warning' : 'secondary'} className="text-[10px]" data-id="element-2040">

                        {selectedReview.priority} Priority
                      </Badge>
                    </div>
                    <CardTitle className="text-lg" data-id="element-2041">
                      {selectedReview.task}
                    </CardTitle>
                    <CardDescription className="mt-1" data-id="element-2042">
                      {selectedReview.matter}
                    </CardDescription>
                  </div>
                  <div className="text-right" data-id="element-2043">
                    <div className="text-xs text-muted-foreground mb-1" data-id="element-2044">
                      AI Confidence
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-2045">
                      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden" data-id="element-2046">
                        <div className={`h-full rounded-full ${selectedReview.aiConfidence > 85 ? 'bg-emerald-500' : selectedReview.aiConfidence > 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                      width: `${selectedReview.aiConfidence}%`
                    }} data-id="element-2047" />

                      </div>
                      <span className="text-sm font-bold" data-id="element-2048">
                        {selectedReview.aiConfidence}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2049">
                {/* Metadata Grid */}
                <div className="grid grid-cols-2 gap-4" data-id="element-2050">
                  <div className="bg-muted/30 p-3 rounded-lg border" data-id="element-2051">
                    <p className="text-xs text-muted-foreground mb-1" data-id="element-2052">
                      Assigned To
                    </p>
                    <p className="text-sm font-medium flex items-center" data-id="element-2053">
                      <User className="w-4 h-4 mr-1.5 text-muted-foreground" data-id="element-2054" />
                      {selectedReview.assignedTo}
                    </p>
                  </div>
                  <div className="bg-muted/30 p-3 rounded-lg border" data-id="element-2055">
                    <p className="text-xs text-muted-foreground mb-1" data-id="element-2056">
                      Created
                    </p>
                    <p className="text-sm font-medium flex items-center" data-id="element-2057">
                      <Clock className="w-4 h-4 mr-1.5 text-muted-foreground" data-id="element-2058" />
                      {selectedReview.date}
                    </p>
                  </div>
                </div>

                {/* Source Document */}
                <div data-id="element-2059">
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center" data-id="element-2060">
                    <FileSearch className="w-4 h-4 mr-2 text-muted-foreground" data-id="element-2061" />
                    Source Document
                  </h4>
                  <div className="bg-muted/30 p-4 rounded-lg border hover:border-blue-300 transition-colors cursor-pointer group" data-id="element-2062">
                    <div className="flex items-center justify-between" data-id="element-2063">
                      <div className="flex items-center space-x-3" data-id="element-2064">
                        <FileText className="w-5 h-5 text-blue-600" data-id="element-2065" />
                        <div data-id="element-2066">
                          <p className="text-sm font-medium text-foreground group-hover:text-blue-600 transition-colors" data-id="element-2067">
                            {selectedReview.sourceDoc}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5" data-id="element-2068">
                            Click to open in document viewer
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-blue-600 transition-colors" data-id="element-2069" />
                    </div>
                  </div>
                </div>

                {/* Issue Description */}
                <div data-id="element-2070">
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center" data-id="element-2071">
                    <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" data-id="element-2072" />
                    Issue Details
                  </h4>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-2073">
                    {selectedReview.type === 'Chronology Conflict' && <p className="text-sm text-amber-900" data-id="element-2074">
                        The system detected a potential conflict in the medical
                        chronology. The source document mentions information
                        that may contradict other records in the matter.
                        Attorney review is required to determine accuracy and
                        relevance.
                      </p>}
                    {selectedReview.type === 'Classification' && <p className="text-sm text-amber-900" data-id="element-2075">
                        The AI classification confidence for this document is
                        below the auto-approval threshold. Manual review is
                        required to verify the document type and ensure proper
                        categorization.
                      </p>}
                    {selectedReview.type === 'Draft Review' && <p className="text-sm text-amber-900" data-id="element-2076">
                        AI-generated draft document requires attorney review
                        before finalization. Please verify all factual
                        statements are supported by source documents and legal
                        arguments are sound.
                      </p>}
                    {selectedReview.type === 'Privilege Flag' && <p className="text-sm text-amber-900" data-id="element-2077">
                        The system detected potential privileged content in this
                        document. Review required to confirm privilege
                        designation and ensure proper handling.
                      </p>}
                  </div>
                </div>

                {/* Preview Section (Conditional based on type) */}
                {selectedReview.type === 'Chronology Conflict' && <div data-id="element-2078">
                    <h4 className="text-sm font-semibold text-foreground mb-2" data-id="element-2079">
                      Chronology Preview
                    </h4>
                    <div className="bg-white border rounded-lg overflow-hidden overflow-x-auto" data-id="element-2080">
                      <table className="w-full text-xs min-w-[400px]" data-id="element-2081">
                        <thead className="bg-muted/50 border-b" data-id="element-2082">
                          <tr data-id="element-2083">
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground" data-id="element-2084">
                              Date
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground" data-id="element-2085">
                              Event
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground" data-id="element-2086">
                              Source
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y" data-id="element-2087">
                          <tr className="hover:bg-muted/30" data-id="element-2088">
                            <td className="px-3 py-2 font-medium" data-id="element-2089">
                              Oct 14, 2023
                            </td>
                            <td className="px-3 py-2" data-id="element-2090">
                              Initial orthopedic consult
                            </td>
                            <td className="px-3 py-2 text-blue-600" data-id="element-2091">
                              RLSF-0056
                            </td>
                          </tr>
                          <tr className="bg-amber-50 border-l-2 border-l-amber-500" data-id="element-2092">
                            <td className="px-3 py-2 font-medium" data-id="element-2093">
                              Oct 14, 2023
                            </td>
                            <td className="px-3 py-2" data-id="element-2094">
                              <span className="bg-amber-200 text-amber-900 px-1 rounded" data-id="element-2095">
                                Patient reported prior mild back pain in 2019
                              </span>
                            </td>
                            <td className="px-3 py-2 text-blue-600" data-id="element-2096">
                              RLSF-0056
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-amber-700 mt-2 flex items-center" data-id="element-2097">
                      <AlertTriangle className="w-3 h-3 mr-1" data-id="element-2098" />
                      This mention may conflict with intake questionnaire
                      stating no prior back issues.
                    </p>
                  </div>}
              </div>

              {/* Action Footer */}
              <div className="p-4 border-t bg-muted/20 shrink-0 flex items-center justify-between" data-id="element-2099">
                <Button variant="outline" size="sm" data-id="element-2100">
                  <Eye className="w-4 h-4 mr-2" data-id="element-2101" />
                  View Full Context
                </Button>
                <div className="flex space-x-2" data-id="element-2102">
                  <Button variant="outline" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-id="element-2103">

                    <X className="w-4 h-4 mr-2" data-id="element-2104" />
                    Reject
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-2105">
                    <Check className="w-4 h-4 mr-2" data-id="element-2106" />
                    Approve
                  </Button>
                </div>
              </div>
            </Card> : <Card className="flex-1 flex items-center justify-center" data-id="element-2107">
              <div className="text-center text-muted-foreground" data-id="element-2108">
                <CheckSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" data-id="element-2109" />
                <p className="text-sm font-medium" data-id="element-2110">Select an item to review</p>
                <p className="text-xs mt-1" data-id="element-2111">
                  Choose from the queue on the left
                </p>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}